// apps/web/src/services/RouletteInventoryService.js

import fetch from 'auth/FetchInterceptor'

const buildQueryString = (params = {}) => {
  const query = new URLSearchParams()

  Object.entries(params).forEach(
    ([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== ''
      ) {
        query.append(key, String(value))
      }
    },
  )

  const queryString = query.toString()

  return queryString
    ? `?${queryString}`
    : ''
}

const unwrapResponse = (response) => {
  return response?.data || response
}

const normalizeList = (response) => {
  const data = unwrapResponse(response)

  if (Array.isArray(data)) {
    return data
  }

  if (Array.isArray(data?.items)) {
    return data.items
  }

  return []
}

const RouletteInventoryService = {
  // =========================================================
  // JOGOS
  // =========================================================

  async getGames() {
    const response = await fetch({
      url: '/games',
      method: 'get',
    })

    return normalizeList(response)
  },

  // =========================================================
  // BRINDES
  // =========================================================

  async getPrizesByGame(gameId) {
    const response = await fetch({
      url: `/prizes${buildQueryString({
        gameId,
      })}`,
      method: 'get',
    })

    return normalizeList(response)
  },

  async getPrize(prizeId) {
    const response = await fetch({
      url: `/prizes/${prizeId}`,
      method: 'get',
    })

    return unwrapResponse(response)
  },

  // =========================================================
  // ESTOQUE
  // =========================================================

  async getStock(prizeId) {
    const response = await fetch({
      url: `/prizes/${prizeId}/stock`,
      method: 'get',
    })

    return unwrapResponse(response)
  },

  async addStock(
    prizeId,
    {
      quantity,
      notes,
    },
  ) {
    const response = await fetch({
      url: `/prizes/${prizeId}/stock/entry`,
      method: 'post',
      data: {
        quantity: Number(quantity),
        notes: notes || null,
      },
    })

    return unwrapResponse(response)
  },

  async removeStock(
    prizeId,
    {
      quantity,
      notes,
    },
  ) {
    const response = await fetch({
      url: `/prizes/${prizeId}/stock/exit`,
      method: 'post',
      data: {
        quantity: Number(quantity),
        notes: notes || null,
      },
    })

    return unwrapResponse(response)
  },

  async returnStock(
    prizeId,
    {
      quantity,
      notes,
    },
  ) {
    const response = await fetch({
      url: `/prizes/${prizeId}/stock/return`,
      method: 'post',
      data: {
        quantity: Number(quantity),
        notes: notes || null,
      },
    })

    return unwrapResponse(response)
  },

  async adjustStock(
    prizeId,
    {
      newStock,
      notes,
    },
  ) {
    const response = await fetch({
      url: `/prizes/${prizeId}/stock/adjust`,
      method: 'post',
      data: {
        newStock: Number(newStock),
        notes: notes || null,
      },
    })

    return unwrapResponse(response)
  },

  async getMovements(
    prizeId,
    params = {},
  ) {
    const response = await fetch({
      url: `/prizes/${prizeId}/movements${buildQueryString(
        params,
      )}`,
      method: 'get',
    })

    return unwrapResponse(response)
  },

  // =========================================================
  // CONFIGURAÇÕES DIÁRIAS
  // =========================================================

  async getConfigurations(prizeId) {
    const response = await fetch({
      url: `/prizes/${prizeId}/configurations`,
      method: 'get',
    })

    return normalizeList(response)
  },

  async getEffectiveConfiguration(
    prizeId,
    date,
  ) {
    const response = await fetch({
      url: `/prizes/${prizeId}/configurations/effective${buildQueryString(
        {
          date,
        },
      )}`,
      method: 'get',
    })

    return unwrapResponse(response)
  },

  async getConfiguration(
    configurationId,
  ) {
    const response = await fetch({
      url: `/prizes/configurations/${configurationId}`,
      method: 'get',
    })

    return unwrapResponse(response)
  },

  async createConfiguration(
    prizeId,
    data,
  ) {
    const response = await fetch({
      url: `/prizes/${prizeId}/configurations`,
      method: 'post',
      data: {
        date: data.date,
        enabled:
          data.enabled ?? true,
        dailyLimit:
          data.dailyLimit === undefined
            ? null
            : data.dailyLimit,
        probabilityWeight:
          data.probabilityWeight ===
          undefined
            ? null
            : data.probabilityWeight,
        notes:
          data.notes || null,
      },
    })

    return unwrapResponse(response)
  },

  async updateConfiguration(
    configurationId,
    data,
  ) {
    const response = await fetch({
      url: `/prizes/configurations/${configurationId}`,
      method: 'patch',
      data: {
        date: data.date,
        enabled: data.enabled,
        dailyLimit:
          data.dailyLimit === undefined
            ? null
            : data.dailyLimit,
        probabilityWeight:
          data.probabilityWeight ===
          undefined
            ? null
            : data.probabilityWeight,
        notes:
          data.notes || null,
      },
    })

    return unwrapResponse(response)
  },

  async deleteConfiguration(
    configurationId,
  ) {
    const response = await fetch({
      url: `/prizes/configurations/${configurationId}`,
      method: 'delete',
    })

    return unwrapResponse(response)
  },

  // =========================================================
  // INVENTÁRIOS
  // =========================================================

  async getInventories(
    prizeId,
    params = {},
  ) {
    const response = await fetch({
      url: `/prizes/${prizeId}/inventories${buildQueryString(
        params,
      )}`,
      method: 'get',
    })

    return unwrapResponse(response)
  },

  async getInventory(inventoryId) {
    const response = await fetch({
      url: `/prizes/inventories/${inventoryId}`,
      method: 'get',
    })

    return unwrapResponse(response)
  },

  async createInventory(
    prizeId,
    data,
  ) {
    const response = await fetch({
      url: `/prizes/${prizeId}/inventories`,
      method: 'post',
      data: {
        countedStock: Number(
          data.countedStock,
        ),
        notes:
          data.notes || null,
      },
    })

    return unwrapResponse(response)
  },

  async deleteInventory(inventoryId) {
    const response = await fetch({
      url: `/prizes/inventories/${inventoryId}`,
      method: 'delete',
    })

    return unwrapResponse(response)
  },

  // =========================================================
  // ENTREGAS
  // =========================================================

  async getPrizeDeliveries(
    prizeId,
    params = {},
  ) {
    const response = await fetch({
      url: `/prizes/${prizeId}/deliveries${buildQueryString(
        params,
      )}`,
      method: 'get',
    })

    return unwrapResponse(response)
  },

  async getGameDeliveries(
    gameId,
    params = {},
  ) {
    const response = await fetch({
      url: `/prizes/games/${gameId}/deliveries${buildQueryString(
        params,
      )}`,
      method: 'get',
    })

    return unwrapResponse(response)
  },

  async getDelivery(deliveryId) {
    const response = await fetch({
      url: `/prizes/deliveries/${deliveryId}`,
      method: 'get',
    })

    return unwrapResponse(response)
  },

  async getDeliveryBySpin(spinId) {
    const response = await fetch({
      url: `/prizes/spins/${spinId}/delivery`,
      method: 'get',
    })

    return unwrapResponse(response)
  },

  async createDelivery(
    spinId,
    data = {},
  ) {
    const response = await fetch({
      url: `/prizes/spins/${spinId}/delivery`,
      method: 'post',
      data: {
        notes:
          data.notes || null,
      },
    })

    return unwrapResponse(response)
  },

  async confirmDelivery(
    deliveryId,
    data = {},
  ) {
    const response = await fetch({
      url: `/prizes/deliveries/${deliveryId}/deliver`,
      method: 'patch',
      data: {
        notes:
          data.notes || null,
      },
    })

    return unwrapResponse(response)
  },

  async cancelDelivery(
    deliveryId,
    data = {},
  ) {
    const response = await fetch({
      url: `/prizes/deliveries/${deliveryId}/cancel`,
      method: 'patch',
      data: {
        notes:
          data.notes || null,
      },
    })

    return unwrapResponse(response)
  },

  async reopenDelivery(deliveryId) {
    const response = await fetch({
      url: `/prizes/deliveries/${deliveryId}/reopen`,
      method: 'patch',
    })

    return unwrapResponse(response)
  },
}

export default RouletteInventoryService